import { dayjs } from '@karve.it/core';
import { createFeature, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';

import { cloneDeep } from 'lodash';

import { Delta } from 'quill/core';

import {
  Asset,
  BaseArtifactFragment,
  BaseCalendarEventFragment,
  BaseFieldFragment,
  BaseUserFragment,
  CalendarEventLocation,
  FullUserFragment,
  InvoiceWithArtifactsFragment,
  ItineraryItem,
  Location,
  UpdateJobInput,
} from '../../generated/graphql.generated';

import { isEstimateArtifact, isInvoiceArtifact } from '../artifacts/artifact.util';
import { zonesFeature } from '../core/zones/zones.reducer';
import { getFieldValue, splitName } from '../fields/fields.utils';
import {
  EventAttendeeRoles,
  EventLocationTypes,
  eventTypeInfoMap,
  FIELD_CONFIG,
  JOB_LOCATION_TYPES,
  JOB_ROLE_MAP,
  JobRole,
} from '../global.constants';
import { getInvoiceName } from '../invoices/invoices.utils';
import { jobsFeature } from '../jobs/jobs.reducer';
import {
  convertToDate,
  formatLocationToDisplay,
  getAccessInformation,
  getBuildingDetails,
  getTimelineV2,
} from '../jobs/jobs.util';
import { safeParseJSON } from '../js';
import { BookingScheduleActions } from '../schedules/booking-schedule/booking-schedule.actions';
import { loadAssetsTriggers } from '../schedules/booking-schedule/booking-schedule.reducer';
import { CREW_LEAD_ROLE } from '../schedules/dispatch/store/dispatch.reducer';
import { ActionSource } from '../state/action-sources';
import { brandingFeature, createConfigSelector, selectEventTypes, selectNavigatorUrlsByEventType } from '../state/branding.store';
import { selectRouteParam } from '../state/router';
import { CallState, LoadingState } from '../utilities/state.util';

import { isDeltaBlank, sortCommentsWithReplies } from './job-activity/comments.utils';
import { jobActivityReducers } from './job-activity/job-activity.reducer';
import { jobActivityInitialState } from './job-activity/job-activity.state';
import { closeJobComponentReducers } from './job-create/close-job-component/close-job-component.reducer';
import { closeJobComponentInitialState } from './job-create/close-job-component/close-job-component.state';
import { JobCreateCustomerActions } from './job-create/job-create-customer/job-create-customer.actions';
import { jobCreateCustomerFeature } from './job-create/job-create-customer/job-create-customer.feature';
import { JobCreateInventoryActions } from './job-create/job-create-inventory/job-create-inventory.actions';
import { jobCreateLocationsReducers } from './job-create/job-create-locations/job-create-locations-state.reducer';
import { JobCreateActions } from './job-create/job-create-state/job-create.actions';
import { jobCreateReducers } from './job-create/job-create-state/job-create.reducer';
import { jobCreateSummaryFeature } from './job-create/job-create-summary/job-create-summary.feature';
import { jobFormActionButtonReducers } from './job-create/job-form-action-button/job-form-action-button.reducers';
import { jobManuallyAddLocationReducers } from './job-create/job-manually-add-location/job-manually-add-location.reducer';
import { jobManuallyAddLocationInitialState } from './job-create/job-manually-add-location/job-manually-add-location.state';
import { jobTimelineReducers } from './job-create/job-timeline-availability/job-timeline-availability.reducers';
import { jobTimelineAvailabilityInitialState } from './job-create/job-timeline-availability/job-timeline-availability.state';
import { jobFilesReducers } from './job-files/job-files.reducers';
import { jobFilesInitialState } from './job-files/job-files.state';
import { jobFinancialsReducers } from './job-financials/job-financials.reducers';
import { jobFinancialsInitialState } from './job-financials/job-financials.state';
import { JobTagActions } from './job-header/job-tags/job-tags.actions';
import { formatDateTimeWithTimezone, NavigatorUrlEvent, resolveNavigatorUrl } from './job-helpers';
import { InventoryActions } from './job-inventory/job-inventory.actions';
import { jobInvoiceReducers } from './job-invoice/job-invoice.reducer';
import { jobAgentReducers } from './job-overview/overview-header/job-agent/job-agent.reducer';
import { jobAgentInitialState } from './job-overview/overview-header/job-agent/job-sagent.state';
import { JobLocationsOverviewActions } from './job-overview/overview-locations/overview-locations.actions';
import { EventItineraryActions } from './job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.actions';
import { BookEventDialogActions } from './job-overview/overview-timeline/timeline-book-event/timeline-book-event.actions';
import { formatEventLocation, jobCreateEventDialogReducers } from './job-overview/overview-timeline/timeline-create-event/timeline-create-event.reducer';
import { jobCreateEventDialogInitialState } from './job-overview/overview-timeline/timeline-create-event/timeline-create-event.state';
import { WorkOrdersActions } from './job-workorders/job-workorders.actions';

export const CALCULATOR_INPUT_METADATA_PREFIX = 'calculatorInput__' as const;
export const CALCULATOR_OUTPUT_METADATA_PREFIX = 'calculatorOutput__' as const;

// Transient discount state (discountPercent, discountApplied) is session-only
// and intentionally excluded from persistence — it should not be restored when
// the job is reopened in a future session.
export const TRANSIENT_CALCULATOR_KEYS = new Set([ 'discountPercent', 'discountApplied' ]);

export type JobToolFields = {
  [key: string]: Partial<BaseFieldFragment>;
};

// States that are tracked by the job tool under "callState"
export const JOB_TOOL_CALL_STATES = [
  'NewJob',
  'Customer',
  'Agent',
  'ScheduleEvents',
  'Events',
  'Tags',
  'Locations',
  'Availability',
  'Comments',
  'ServiceArea',
  'SuggestedCustomers',
  'AiCharges',
  'AvailableDiscounts',
  'YemboSelfSurvey',
  'YemboMove',
  'Assets',
  'Distances',
] as const;

export type JobToolCallState = (typeof JOB_TOOL_CALL_STATES)[number];

export type LocationWithHowSetIndicator = {
  [key: string]: {
    selectedLocation: Partial<Location> & { setManually?: boolean },
    address: string,
    unitNumber: string,
  };
};

export interface Summary {
  text?: string;
  contents?: Delta;
}

export type Modes = 'edit' | 'create';

export const jobChangeNamespaces = ['fieldsInput', 'customerInput', 'locationsInputs', 'jobInput', 'inventoryInput'] as const;

export type JobChangeNamespace = (typeof jobChangeNamespaces)[number];

export type FullUserFragmentWithFullName = Partial<FullUserFragment> & {
  fullName?: string;
};

export type Tag = {
  id: string;
  name: string;
}

export type EstimateMethod = 'selfSurvey' | 'smartConsult';

export enum NewOpportunityCard {
  createCustomer = 'create customer',
  createLocations = 'create locations',
  createInventory = 'create inventory',
  timelineAndAvailability = 'timeline and availability',
  summary = 'summary'
};

export type UserModifiedTravelTime = Partial<Record<EventLocationTypes, {
    estimatedTimeAtLocation: number;
    travelTimeToNextLocation: number;
}>>;

/***
 * The initial job tool state, also used to
 * create the job tool type.
 *
 * PLEASE make sure you place any new states in the respective category
 * or try to create a new category as this state is becoming really large.
 */
export const jobToolInitialState = {

  //=== Shared info

  itinerary: undefined as ItineraryItem[],
  callState: JOB_TOOL_CALL_STATES.reduce((p, c) => ({ ...p, [c]: LoadingState.INIT }), {} as {
    [ key in JobToolCallState ]: CallState;
  }),

  agentInstructions: null as string,

  //=== Job Creation Tool
  // User has made changes in the new opportunity tool but hasn't created a job yet
  collapsedNewOpportunityCards: {} as Partial<Record<NewOpportunityCard, true>>,

  ...jobAgentInitialState,
  ...jobCreateEventDialogInitialState,
  ...jobManuallyAddLocationInitialState,
  ...jobFinancialsInitialState,
  ...jobActivityInitialState,
  ...closeJobComponentInitialState,
  ...jobTimelineAvailabilityInitialState,
  ...jobFilesInitialState,
} as const;

export type JobToolState = typeof jobToolInitialState;

export type LoadingSelectorType = MemoizedSelector<Record<string, any>, boolean, (s1: JobToolState['callState']) => boolean>;

export type LoadingErrorSelectorType = MemoizedSelector<Record<string, any>, boolean, (s1: JobToolState['callState']) => string | null>;

export interface UserWithName extends BaseUserFragment {
  name: string;
  role?: JobRole;
}


export interface UpdateJobsV2Input {
  jobId: string;
  addUsers: UserWithName[];
  removeUsers: UserWithName[];
}

export interface JobsV2EventLocation {
  id: string;
  locationId: string;
  type: string;
  address: string;
  addressLabel: string;
  order: number;
  estimatedTimeAtLocation: number;
  totalTravelTimeToNextLocation: number;
  ignoreInDuration: boolean;
  originalLocation?: DetailedLocation;
}

export interface UpdateJobsV2CalendarEventInput {
  eventId: string;
  title?: string;
  status?: string;
  type?: string;
  setAttendees?: {
    addAttendees: UserWithName[];
    removeAttendees: UserWithName[];
  };
  setAssets?: {
    addAssets: Asset[];
    removeAssets: Asset[];
  };
  setLocations?: {
    addLocations: JobsV2EventLocation[];
    editLocations: JobsV2EventLocation[];
    removeLocations: JobsV2EventLocation[];
  };
};

export interface JobEvent {
  title: string;
  details: string;
  datetime: string;
  style: { borderLeftColor: string };
  locations: JobsV2EventLocation[];
  crew: UserWithName[];
  event: BaseCalendarEventFragment;
  assetDetails: string;
  totalCrew: string;
}

export const formatPotentialEventLocations = (location: DetailedLocation, index: number): JobsV2EventLocation => ({
  address: location.addressLineOne,
  addressLabel: `${location.locationType}: ${location.addressLineOne}`,
  id: location.id,
  locationId: location.id,
  type: location.locationType,
  estimatedTimeAtLocation: 0,
  totalTravelTimeToNextLocation: 0,
  order: index,
  ignoreInDuration: true,
  originalLocation: location,
});

export const formatEventLocations = (location: CalendarEventLocation): JobsV2EventLocation => ({
  address: location.location.addressLineOne,
  addressLabel: `${location.type}: ${location.location.addressLineOne}`,
  id: location.id,
  locationId: location.location.id,
  type: location.type,
  estimatedTimeAtLocation: location.estimatedTimeAtLocation,
  totalTravelTimeToNextLocation: location.travelTimeToNextLocation + location.travelTimeToNextLocationOffset,
  ignoreInDuration: true,
  order: location.order,
});

const getAssetDetails = (assets: BaseCalendarEventFragment['assets'] = []) => {
  if (!assets.length) return '';

  const assetTypes = Array.from(new Set(assets.map(asset => asset.type)));
  const assetDetails = assetTypes.map(assetType => {
    const filteredAssets = assets.filter(asset => asset.type === assetType);
    const assetNames = filteredAssets.map(asset => asset.name || assetType).join(', ');
    return `${filteredAssets.length} ${filteredAssets.length > 1 ? `${assetType}s` : assetType} (${assetNames})`;
  });

  return assetDetails.join(' | ');
};

export const formatEventDetails = (event: BaseCalendarEventFragment, timezone: string): JobEvent => {
  const { title: eventTitle, status, assets = [], attendees = [], start, end, locations = [] } = event;

  const title = [eventTitle, status].filter(Boolean).join(' - ');

  let eventDate = 'Not Booked';
  let eventTime = null;
  let duration = null;

  if (start && end) {
    const startDateFormatted = formatDateTimeWithTimezone(start, timezone);
    const endDateFormatted = formatDateTimeWithTimezone(end, timezone);

    eventDate = startDateFormatted.date === endDateFormatted.date ?
      endDateFormatted.date :
      `${startDateFormatted.date} - ${endDateFormatted.date}`;

    eventTime = `${startDateFormatted.time} - ${endDateFormatted.time}`;

    const dayJsStart = dayjs(start * 1000).tz(timezone);
    const dayJsEnd = dayjs(end * 1000).tz(timezone);
    duration = dayJsEnd.diff(dayJsStart, 'hour');
  }

  const assetDetails = getAssetDetails(assets);


  const crew = attendees.filter(({ role }) => role !== EventAttendeeRoles.author);
  const crewCount = crew.length;
  const totalCrew = crewCount ? `${crewCount} Crew` : '';

  const datetime = [
    eventDate,
    eventTime,
    duration && `${duration} hrs`,
  ].filter(Boolean).join(' | ');

  const details = [
    eventDate,
    eventTime,
    duration && `${duration} hrs`,
    //timezone && duration ? `(${timezone})` : '',
    assetDetails,
    totalCrew
  ].filter(Boolean).join(' | ');

  const style = {
    borderLeftColor: eventTypeInfoMap[event.type]?.backgroundColor,
  };

  // Event details for editing an event
  const derivedCrew = crew.map((attendee) => ({
    ...attendee.user,
    role: attendee.role as JobRole,
    name: [attendee.user.givenName, attendee.user.familyName].filter(Boolean).join(' '),
  }));

  const derivedLocations = locations
    ? locations.map(formatEventLocations).sort((a, b) => a.order - b.order)
    : [];

  return {
    title,
    datetime,
    details,
    style,
    event,
    assetDetails,
    totalCrew,
    crew: derivedCrew,
    locations: derivedLocations,
  };
};

export function getUserFullName(user: FullUserFragment) {
  return [user.givenName, user.familyName].filter(Boolean).join(' ');
}

export const getAttendeeRole = (attendee: FullUserFragment): EventAttendeeRoles => {
  const role = attendee.roles?.[0];
  return role?.attributes.includes(CREW_LEAD_ROLE)
    ? EventAttendeeRoles.crewLead
    : EventAttendeeRoles.crewMember;
}

export type JobSummary = {
  summary: Delta;
  format: 'Object';
  isEmpty: boolean;
} |
{
  summary: string;
  format: 'text';
  isEmpty: boolean;
};

export interface JobSummaries {
  crewSummary: JobSummary;
  adminSummary: JobSummary;
  customerSummary: JobSummary;
}

export const parseSummary = (
  summary: { text: string; contents: string },
  summaryType: 'admin' | 'crew' | 'customer',
  readOnly: boolean = false,
): JobSummary => {

  if (!summary) {
    return {
      summary: getEmptySummaryText(summaryType, readOnly),
      format: 'text',
      isEmpty: true,
    };
  }

  const { contents, text } = summary;

  if (!contents) {
    return {
      summary: text || getEmptySummaryText(summaryType, readOnly),
      format: 'text',
      isEmpty: !text,
    };
  }

  let delta: Delta | null = null;
  try {
    delta = JSON.parse(contents) as Delta;
  } catch {
    delta = null;
  }

  if (!delta || !Array.isArray(delta.ops) || !delta.ops.length) {
    return {
      summary: text || getEmptySummaryText(summaryType, readOnly),
      format: 'text',
      isEmpty: !text,
    };
  }

  return {
    summary: delta,
    format: 'Object',
    isEmpty: false,
  }
};

function getEmptySummaryText(summaryType: 'admin' | 'crew' | 'customer', readOnly: boolean) {

  if (readOnly) {
    return 'Summary is not set';
  } else {
    let visibleTo = '';

    if (summaryType === 'admin') {
      visibleTo = 'only visible for admins';
    } else if (summaryType === 'crew') {
      visibleTo = 'visible for crew and office staff';
    } else if (summaryType === 'customer') {
      visibleTo = 'visible for customers';
    }

    return `Click here to set the ${summaryType} summary, ${visibleTo}.`;
  }
}

export const jobToolFeature = createFeature({
  name: 'jobTool',
    extraSelectors: ({ selectCallState }) => {

    const selectLoadingStates = JOB_TOOL_CALL_STATES.reduce(
      (p, callState) => ({
        ...p,
        [`select${callState}Loading`]: createSelector(
          selectCallState,
          (state) =>
          state[callState] === LoadingState.PENDING ||
            state[callState] === LoadingState.LOADING ||
            state[callState] === LoadingState.MUTATING,
        ),
        [`select${callState}Mutating`]: createSelector(
          selectCallState,
          (state) =>
          state[callState] === LoadingState.MUTATING,
        ),
        [`select${callState}Loaded`]: createSelector(
          selectCallState,
          (state) => state[callState] === LoadingState.LOADED || state[callState] === LoadingState.MUTATED,
        ),
      }),
      {} as {
        [x in
          | `select${JobToolCallState}Loading`
          | `select${JobToolCallState}Mutating`
          | `select${JobToolCallState}Loaded`
        ]: LoadingSelectorType;
      },
    );

    const selectors = {
      ...selectLoadingStates,
    }

    return selectors;
  },
  reducer: createReducer(
    jobToolInitialState as JobToolState,
    on(JobCreateCustomerActions.jobCustomerLoaded, (state: JobToolState, { job }): JobToolState => {
      return {
        ...state,
        movingDate: convertToDate(job.timeline),
      };
    }),
    on(JobCreateCustomerActions.searchUsersSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          SuggestedCustomers: LoadingState.LOADED,
        },
      };
    }),
    on(JobCreateCustomerActions.searchUsersError, (state: JobToolState, res): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          SuggestedCustomers: {
            error: res.error.message,
          },
        },
      };
    }),
    on(JobCreateCustomerActions.acceptCustomerChanges, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Customer: LoadingState.MUTATING,
        },
      };
    }),
    on(JobCreateCustomerActions.updateCustomerDataSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Customer: LoadingState.MUTATED,
        },
      };
    }),
    on(JobCreateCustomerActions.updateCustomerDataError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Customer: { error },
        },
      };
    }),
    on(JobCreateCustomerActions.headerClicked, (state: JobToolState): JobToolState => {
      const collapsedNewOpportunityCards = { ...state.collapsedNewOpportunityCards };

      if (NewOpportunityCard.createCustomer in collapsedNewOpportunityCards) {
        delete collapsedNewOpportunityCards[NewOpportunityCard.createCustomer];
      } else {
        collapsedNewOpportunityCards[NewOpportunityCard.createCustomer] = true;
      }

      return {
        ...state,
        collapsedNewOpportunityCards,
      };
    }),
    on(
      InventoryActions.createSelfSurveyLinkConfirmationDialogClosed,
      (state: JobToolState, { confirmed }): JobToolState => {
        if (!confirmed) return state;

        return {
          ...state,
          callState: {
            ...state.callState,
            YemboSelfSurvey: LoadingState.MUTATING,
          },
        };
      },
    ),
    on(InventoryActions.createSelfSurveyLinkSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          YemboSelfSurvey: LoadingState.MUTATED,
        },
      };
    }),
    on(InventoryActions.createSelfSurveyLinkError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          YemboSelfSurvey: { error },
        },
      };
    }),
    on(InventoryActions.startOnsiteConfirmButtonClicked, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          YemboSelfSurvey: LoadingState.MUTATING,
        },
      };
    }),
    on(InventoryActions.startOnsiteSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          YemboSelfSurvey: LoadingState.MUTATED,
        },
      };
    }),
    on(InventoryActions.startOnsiteError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          YemboSelfSurvey: {
            error,
          },
        },
      };
    }),
    on(JobCreateInventoryActions.headerClicked, (state: JobToolState): JobToolState => {
      const collapsedNewOpportunityCards = { ...state.collapsedNewOpportunityCards };

      if (NewOpportunityCard.createInventory in collapsedNewOpportunityCards) {
        delete collapsedNewOpportunityCards[NewOpportunityCard.createInventory];
      } else {
        collapsedNewOpportunityCards[NewOpportunityCard.createInventory] = true;
      }

      return {
        ...state,
        collapsedNewOpportunityCards,
      };
    }),
    on(JobCreateActions.addStopGainedFocus, (state: JobToolState): JobToolState => {
      const collapsedNewOpportunityCards = { ...state.collapsedNewOpportunityCards };

      delete collapsedNewOpportunityCards[NewOpportunityCard.createInventory];

      return {
        ...state,
        collapsedNewOpportunityCards,
      };
    }),
    on(
      JobTagActions.setTagsSuccess,
      JobTagActions.setTagsError,
      (state: JobToolState): JobToolState => {
        return {
          ...state,
          callState: {
            ...state.callState,
            Tags: LoadingState.MUTATED,
          },
        };
      },
    ),
    on(JobTagActions.inPlaceDeactivated, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Tags: LoadingState.MUTATING,
        },
      };
    }),
    on(WorkOrdersActions.loadAvailableDiscountsSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          AvailableDiscounts: LoadingState.LOADED,
        },
      };
    }),
    on(WorkOrdersActions.generateChargesButtonClicked, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          AiCharges: LoadingState.MUTATING,
        },
      };
    }),
    on(
      WorkOrdersActions.generateChargesWithAISuccess,
      (state: JobToolState, { itinerary, agentInstructions }): JobToolState => {
        return {
          ...state,
          itinerary,
          agentInstructions,
          callState: {
            ...state.callState,
            AiCharges: LoadingState.MUTATED,
          },
        };
      },
    ),
    on(WorkOrdersActions.generateChargesWithAIError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          AiCharges: { error },
        },
      };
    }),
    on(BookEventDialogActions.findAvailabilityRequested, (state: JobToolState): JobToolState => ({
      ...state,
      callState: {
        ...state.callState,
        Availability: LoadingState.LOADING,
      },
    })),
    on(BookEventDialogActions.findAvailabilitySuccess, (state: JobToolState): JobToolState => ({
      ...state,
      callState: {
        ...state.callState,
        Availability: LoadingState.LOADED,
      },
    })),
    on(BookEventDialogActions.bookButtonClicked, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Events: LoadingState.MUTATING,
        },
      };
    }),
    on(BookEventDialogActions.bookSmartConsultForDefaultAdminEmailCancelButtonClicked, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Events: LoadingState.INIT,
        },
      };
    }),
    on(
      BookEventDialogActions.bookEventSuccess,
      BookEventDialogActions.rescheduleEventSuccess,
      (state: JobToolState): JobToolState => ({
        ...state,
        callState: {
          ...state.callState,
          Events: LoadingState.MUTATED,
        },
      }),
    ),
    on(
      BookEventDialogActions.bookEventError,
      BookEventDialogActions.rescheduleEventError,
      (state: JobToolState, { error }): JobToolState => ({
        ...state,
        callState: {
          ...state.callState,
          Events: { error },
        },
      }),
    ),
    on(EventItineraryActions.saveEventChangesStarted, (state: JobToolState): JobToolState => ({
      ...state,
      callState: {
        ...state.callState,
        Events: LoadingState.MUTATING,
      },
    })),
    on(EventItineraryActions.saveEventChangesSuccess, (state: JobToolState): JobToolState => ({
      ...state,
      callState: {
        ...state.callState,
        Events: LoadingState.MUTATED,
      },
    })),
    on(EventItineraryActions.saveEventChangesError, (state: JobToolState, { error }): JobToolState => ({
      ...state,
      callState: {
        ...state.callState,
        Events: { error },
      },
    })),
    on(...loadAssetsTriggers, (state: JobToolState): JobToolState => ({
      ...state,
      callState: {
        ...state.callState,
        Assets: LoadingState.LOADING,
      },
    })),
    on(BookingScheduleActions.loadAssetsSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Assets: LoadingState.LOADED,
        },
      };
    }),
    on(BookingScheduleActions.loadAssetsError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Assets: { error },
        },
      };
    }),
    on(BookingScheduleActions.loadEventsSuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          ScheduleEvents: LoadingState.LOADED,
        },
      };
    }),
    on(BookingScheduleActions.loadEventsError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Events: { error },
        },
      };
    }),
    on(BookingScheduleActions.reloadRequired, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Availability: LoadingState.LOADING,
          ScheduleEvents: LoadingState.LOADING,
        },
      };
    }),
    on(BookingScheduleActions.loadAvailabilitySuccess, (state: JobToolState): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Availability: LoadingState.LOADED,
        },
      };
    }),
    on(BookingScheduleActions.loadAvailabilityError, (state: JobToolState, { error }): JobToolState => {
      return {
        ...state,
        callState: {
          ...state.callState,
          Availability: { error },
        },
      };
    }),
    on(
      JobLocationsOverviewActions.locationChangesDetected,
      (state: JobToolState): JobToolState => {

        return {
          ...state,
          callState: {
            ...state.callState,
            Distances: LoadingState.LOADING,
          },
        }
      },
    ),
    on(
      JobLocationsOverviewActions.calculateDistancesSuccess,
      (state: JobToolState): JobToolState => {

        return {
          ...state,
          callState: {
            ...state.callState,
            Distances: LoadingState.LOADED,
          },
        }
      },
    ),
    on(
      JobLocationsOverviewActions.calculateDistancesError,
      (state: JobToolState, { error }): JobToolState => {

        return {
          ...state,
          callState: {
            ...state.callState,
            Distances: { error },
          },
        }
      },
    ),
    on(
      JobLocationsOverviewActions.addLocationManuallyButtonClicked,
      (state: JobToolState, { locationType }): JobToolState => {
        return {
          ...state,
          manuallyAddLocationDialogVisible: true,
          manuallyAddLocationDialog: {
            ...jobManuallyAddLocationInitialState.manuallyAddLocationDialog,
            locationType,
          },
        }
      },
    ),
    ...jobAgentReducers,
    ...closeJobComponentReducers,
    ...jobCreateLocationsReducers,
    ...jobCreateEventDialogReducers,
    ...jobManuallyAddLocationReducers,
    ...jobFinancialsReducers,
    ...jobInvoiceReducers,
    ...jobActivityReducers,
    ...jobTimelineReducers,
    ...jobCreateReducers,
    ...jobFilesReducers,
    ...jobFormActionButtonReducers,
  ),
});

export const selectSummaryIndex = createSelector(
  jobCreateSummaryFeature.selectSummaryEditors,
  (summaryEditors) => summaryEditors.summaryIndex,
);

export const UNSAVEABLE_JOB_PAGE_CHANGE_SOURCES = [
  ActionSource.ACTIVITY,
];

export const selectJobId = selectRouteParam('jobId');

export const selectJob = createSelector(
  selectJobId,
  jobsFeature.selectJobs,
  (jobId, jobs) => jobs[jobId],
);


export const selectJobFormMode = createSelector(
  selectJob,
  (job) => Boolean(job) ? 'edit' : 'create',
);

export const selectJobCallState = createSelector(
  selectJobId,
  jobsFeature.selectCallState,
  (jobId, callState) => callState[jobId] || LoadingState.INIT,
);

export const selectJobUpdating = createSelector(
  selectJobCallState,
  (callState) => callState === LoadingState.MUTATING,
);

export const selectJobLoading = createSelector(
  selectJobCallState,
  (callState) => 
    callState === LoadingState.PENDING ||
    callState === LoadingState.LOADING ||
    callState === LoadingState.MUTATING,
);

export const selectJobLoaded = createSelector(
  selectJobCallState,
  (callState) => callState === LoadingState.LOADED || callState === LoadingState.MUTATED,
);

export const selectJobZone = createSelector(selectJob, (job) => {
  return job?.zone;
});

export const jobFranchiseOf = (job: ReturnType<typeof selectJob> | undefined) => {
  const isArea = job?.zone?.type === 'area';

  if (isArea) {
    return job?.zone?.parent;
  } else {
    return job?.zone;
  }
};

export const selectJobFranchise = createSelector(selectJob, jobFranchiseOf);

export const selectJobFranchiseId = createSelector(selectJobFranchise, (jobFranchise) => jobFranchise?.id);

export const selectJobInCurrentZone = createSelector(
  selectJobFranchiseId,
  zonesFeature.selectCurrentAppZone,
  (jobFranchiseId, currentZone) => {
    if (!jobFranchiseId || !currentZone) return true;
    return jobFranchiseId === currentZone.id;
  }
);

export type JobZoneWarningType = 'shared' | 'above-zone' | 'foreign-zone';

export interface JobZoneWarning {
  text: string;
  type: JobZoneWarningType;
}

/**
 * Why a job cannot be edited from the current zone, or undefined when it can.
 *
 * Lives here rather than on a page component because two pages read it: the job
 * page renders all three warnings, and the New Opportunity page renders only
 * `above-zone` (the one carrying the Import button).
 *
 * `isShared` is deliberately checked first. A job can be both shared into the
 * current zone and above it, and importing someone else's shared job is the
 * wrong action — so the shared warning must win.
 */
export const selectJobEditDisabledWarning = createSelector(
  selectJobInCurrentZone,
  zonesFeature.selectCurrentAppZone,
  selectJob,
  selectJobFranchise,
  (inZone, currentZone, job, jobFranchise): JobZoneWarning | undefined => {

    if (inZone || !currentZone || !job || !jobFranchise) return;

    const isShared = job?.events?.some((e) => e?.sharedZones?.includes(currentZone?.id));

    const isAboveZone = currentZone?.zoneDepth > jobFranchise?.zoneDepth;

    if (isShared) {
      return {
        text: 'This job is shared. It can only be edited from its original zone.',
        type: 'shared',
      }
    } else if (isAboveZone) {
      return {
        text: 'This job is above your zone. Import it to your zone to start editing.',
        type: 'above-zone',
      }
    } else {
      return {
        text: 'This job belongs to another zone. You cannot edit it.',
        type: 'foreign-zone',
      }
    }
  },
);

export const selectInvoices = createSelector(selectJob, (job) => job?.invoices || []);

export const selectInvoicesDictionary = createSelector(selectInvoices, (invoices) => invoices.reduce(
  (curr, invoice) => {
    curr[invoice.id] = invoice;
    return curr;
  },
  {} as { [invoiceId: string]: InvoiceWithArtifactsFragment},
));

export const selectArtifactsDictionary = createSelector(selectJob, (job) => job.artifacts.reduce(
  (curr, artifact) => {
    curr[artifact.id] = artifact;
    return curr;
  },
  {} as { [invoiceId: string]: BaseArtifactFragment},
))

export const selectActionsMenuInvoice = createSelector(
  jobToolFeature.selectActionsMenuInvoiceId,
  selectInvoices,
  (invoiceId, invoices) => invoices.find((i) => i.id === invoiceId),
);

export const selectComments = createSelector(selectJob, (job) => {
  if (!job || !job.comments.comments) {
    return [];
  }

  return sortCommentsWithReplies(job.comments.comments, false);
});

export const selectCustomer = createSelector(selectJob, (job) => {
  const jobCustomer = job?.users?.find((u) => u.role === JOB_ROLE_MAP.customerRole);
  return jobCustomer?.user;
});

export const selectCustomerName = createSelector(selectCustomer, (customer) => {
  if (!customer) return 'No Customer';

  const customerName = [
    customer.givenName,
    customer.familyName,
  ]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(' ');
  return customerName;
});

export const selectCompanyName = createSelector(selectCustomer, (customer) => {
  return customer?.company;
});

export const selectJobSummaries = createSelector(selectJob, (job) => {
  return {
    crewSummary: parseSummary(job?.crewSummary, 'crew'),
    adminSummary: parseSummary(job?.adminSummary, 'admin'),
    customerSummary: parseSummary(job?.customerSummary, 'customer'),
  };
});

export const selectJobMetadata = createSelector(selectJob, (job) => {
  return {
    jobOrigin: job?.metadata?.jobOrigin,
    customerType: job?.metadata?.jobType,
  };
});

export const selectJobSource = createSelector(selectJob, (job) => {
  const sourceField = job?.fields?.find((field) => field.name === 'customer.howDidTheyHearAboutUs');
  const source = sourceField?.values?.[0]?.value;

  return source;
});

export const selectJobTimezone = createSelector(
  selectJob,
  (job) => {
    return job?.zone?.timezone;
  }
);

export const selectJobLocations = createSelector(selectJob, (job) => {
  const jobLocations = {
    otherStops: [],
  } as JobLocations;

  if (!job) {
    return jobLocations;
  }

  job?.locations?.forEach((locationObject) => {
    const { locationType, location } = locationObject;
    if (['start', 'end', 'dock'].includes(locationType)) {
      const addressString = formatLocationToDisplay(locationObject.location);
      jobLocations[locationType] = {
        ...location,
        locationType,
        addressString,
      };
    } else {
      jobLocations.otherStops.push({
        ...location,
        locationType,
        addressString: formatLocationToDisplay(locationObject.location),
      } as DetailedLocation);
    }
  });

  const locationFields = {
    startLocation: {
      parkingInformation: '',
    },
    endLocation: {
      parkingInformation: '',
    },
  };

  // Process each field and assign to respective location object
  job?.fields?.forEach((field) => {
    if (field.name.startsWith('startLocation.') || field.name.startsWith('endLocation.')) {
      const { namespace, key } = splitName(field as BaseFieldFragment);
      locationFields[namespace][key] = getFieldValue(field);
    }
  });

  if (jobLocations.start) {
    jobLocations.start.details = getBuildingDetails(locationFields.startLocation);
    jobLocations.start.parkingInformation = locationFields.startLocation?.parkingInformation;
    jobLocations.start.accessInformation = getAccessInformation(locationFields.startLocation);
  }

  if (jobLocations.end) {
    jobLocations.end.details = getBuildingDetails(locationFields.endLocation);
    jobLocations.end.parkingInformation = locationFields.endLocation?.parkingInformation;
    jobLocations.end.accessInformation = getAccessInformation(locationFields.endLocation);
  }

  return jobLocations;
});

export const selectPotentialEventLocations = createSelector(
  selectJobLocations,
  selectJob,
  jobToolFeature.selectCreateEventDialog,
  (jobLocations, job, { selectedEventId }): JobsV2EventLocation[] => {

  if (!jobLocations) return [];

  const { dock, start, end, otherStops = [] } = jobLocations;

  const formattedJobLocations = [
      dock && { ...dock, locationType: 'basestart' },
      start && { ...start },
      end && { ...end },
      ...otherStops,
      dock && { ...dock, locationType: 'baseend' },
  ].filter(Boolean).map(formatPotentialEventLocations);

  const event = (job.events || []).find((e) => e.id === selectedEventId);

  if (!event) {
    return formattedJobLocations;
  }

  const eventLocations = (event.locations || []).map(formatEventLocation);

  const locations: JobsV2EventLocation[] = [];

  for (const jobLocation of formattedJobLocations) {

    const eventLocation = eventLocations.find((e) => e.type === jobLocation.type);

    if (eventLocation) {
      locations.push(eventLocation);
    } else {
      locations.push(jobLocation);
    }
  }

  return locations;
},
);

export const selectJobEvents = createSelector(selectJob, selectJobTimezone, (job, jobTimezone) => {
  // Create a shallow copy before sorting to avoid mutating the original array
  const events = (job?.events || []).slice();

  return events
      .sort((a, b) => a.sequentialOrder - b.sequentialOrder)
      .map(event => formatEventDetails(event, jobTimezone))
});

export const selectLatestAndOlderEstimates = createSelector(selectJob, (job) => {
  const estimates = cloneDeep(job?.artifacts || [])
    .filter((doc) => isEstimateArtifact(doc))
    .sort((a, b) => b.createdAt - a.createdAt);

  const [ latestEstimate, ...olderEstimates ] = estimates;

  return {
    latestEstimate,
    olderEstimates,
  };
});

export const selectLatestEstimate = createSelector(selectLatestAndOlderEstimates, (estimates) => estimates.latestEstimate);

export const selectOlderEstimates = createSelector(selectLatestAndOlderEstimates, (estimates) => estimates.olderEstimates);

export const selectInvoicesSortedByDate = createSelector(selectJob, (job) => {
  return cloneDeep(job?.invoices || []).sort((a, b) => b.createdAt - a.createdAt);
});

export const selectTransactions = createSelector(selectJob, (job) => job?.transactions || []);

export const selectLatestAndOlderInvoices = createSelector(selectInvoicesSortedByDate,  (invoices) => {
  const [ latestInvoice, ...olderInvoices ] = invoices;

  return {
    latestInvoice,
    olderInvoices,
  };
});

export const selectLatestInvoice = createSelector(selectLatestAndOlderInvoices, (invoices) => invoices.latestInvoice);

export const selectOlderInvoices = createSelector(selectLatestAndOlderInvoices,(invoices) => invoices.olderInvoices);

export const selectTransactionsWithInvoiceNames = createSelector(
  selectJob,
  jobToolFeature.selectCollapsedTransactionRemarks,
  (job, collapsedRemarks) => {
    if (!job?.transactions) {
      return [];
    }
   
    return job.transactions
      .map((transaction) => {
        const remarks = safeParseJSON(transaction.remarks, new Delta());
        const hasRemarks = !isDeltaBlank(remarks);

        return {
          ...transaction,
          invoiceName: getInvoiceName(transaction.invoice, job),
          remarks,
          hasRemarks,
          expandRemarks: !collapsedRemarks[transaction.id],
        };
      })
      .sort((a, b) => b.createdAt - a.createdAt);
  }
);

export const selectOtherDocumentsExceptInvoice = createSelector(selectJob, (job) => {
  return (job?.artifacts || [])
    .filter((doc) => !isInvoiceArtifact(doc))
    .sort((a, b) => b.createdAt - a.createdAt);
});

export const selectIsYemboMoveCreated = createSelector(selectJob, (job) => {
  const { yemboMoveCreatorId = '', yemboEmployeeLink = '', yemboCustomerLink = '' } = job?.metadata || {};
  return Boolean(yemboMoveCreatorId || (yemboEmployeeLink && yemboCustomerLink));
});

export const selectQubeSheetsProjectId = createSelector(selectJob, (job) => {
  const virtualEstimateEvent = (job?.events || []).find((e) => e.type === 'virtualEstimate' && e.metadata?.qubeSheetsProjectId);
  return virtualEstimateEvent?.metadata?.qubeSheetsProjectId as string | undefined;
});

export const selectSendDocumentDisabled = createSelector(selectCustomer, (customer) => {
  return !customer?.email && !customer?.phone;
});

export const selectUpdateJobInputFromCustomerEditor = createSelector(
	selectJob,
	jobCreateCustomerFeature.selectCustomerEditor,
	jobToolFeature.selectMovingDate,
	(job, { jobOrigin, customerType, referralSource }, movingDate): UpdateJobInput => {

		const updateJobs: UpdateJobInput = {
			jobId: job.id,
			metadata: {},
			timeline: getTimelineV2(movingDate).timeline,
		}

		if (referralSource) {
			updateJobs.fields = {
				objectLabel: 'Job',
				fields: [{
					fieldName: FIELD_CONFIG.customer.howHeard.name,
					value: referralSource,
				}],
			}
		}

		if (jobOrigin) {
			updateJobs.metadata.jobOrigin = jobOrigin;
		}

		if (customerType) {
			updateJobs.metadata.jobType = customerType;
		}

		return updateJobs;
	}
);

export const selectIsBusinessGroupCustomer = createSelector(
  jobCreateCustomerFeature.selectCustomerEditor,
  ({ selectedCustomer }): { isBusinessGroup: boolean; businessGroupId: string | null } => {
    if (!selectedCustomer?.roles) {
      return { isBusinessGroup: false, businessGroupId: null };
    }

    //if at least one role is from business group
    for (const role of selectedCustomer.roles) {
      for (const zone of role?.zones?.nodes || []) {
        if (zone?.attributes?.includes('business-group')) {
          return { isBusinessGroup: true, businessGroupId: zone.id || null };
        }
      }
    }

    return { isBusinessGroup: false, businessGroupId: null };
  }
);

//if job already moved to franchise zone
export const selectBusinessGroupCustomerIsAboveTheCurrentJobZone = createSelector(
  selectIsBusinessGroupCustomer,
  selectJobFranchiseId,
  ({ isBusinessGroup, businessGroupId }, jobFranchiseId): boolean => {

    if (!isBusinessGroup) {
      return false;
    } else if (businessGroupId === jobFranchiseId) {
      return false;
    } else {
      return true;
    }
  }
);

export const selectCustomerEditable = createSelector(
  selectIsBusinessGroupCustomer,
  brandingFeature.selectAvailableZones,
  selectBusinessGroupCustomerIsAboveTheCurrentJobZone,
  ({ isBusinessGroup, businessGroupId }, availableZones, customerIsAboveJobZone): {
    customerEditable: boolean,
    notEditableWarning: string,
  } => {

    const customerCanBeEdited = availableZones?.some(zone => zone.id === businessGroupId);

    if (!isBusinessGroup || !customerIsAboveJobZone) {
      return {
        customerEditable: true,
        notEditableWarning: '',
      };
    } else {
      return {
        customerEditable: customerCanBeEdited,
        notEditableWarning: `Missing permissions to edit customer from ${businessGroupId} zone`
      };
    }
  }
);

export const selectCalculatorUrl = createSelector(
  createConfigSelector('CALCULATOR_URL'),
  selectJob,
  (calculatorUrl, job) => job?.metadata?.calculatorUrl || calculatorUrl,
);

export const selectOpsNotes = createSelector(
  selectJob,
  (job) => job?.metadata?.calculatorOutput__opsNotes ?? '',
);

/**
 * Resolve the navigator script URL for a single event. Shares `resolveNavigatorUrl` with
 * the button-visibility selectors, so what is offered and what launches always agree.
 */
export const selectNavigatorUrlForEvent = (event: NavigatorUrlEvent | undefined) => createSelector(
  createConfigSelector('NAVIGATOR_URL'),
  selectNavigatorUrlsByEventType,
  selectEventTypes,
  selectJob,
  (defaultUrl, typeUrls, eventTypes, job) => resolveNavigatorUrl(event, {
    jobNavigatorUrl: job?.metadata?.navigatorUrl,
    typeUrls,
    eventTypes,
    defaultUrl,
  }),
);

export const selectOrderedJobLocationTypes = createSelector(
  selectJob,
  (job) => {

    const currentJobLocationTypes = JOB_LOCATION_TYPES.map((l) => l.key);

    if (!job || !job.locations) {
      return currentJobLocationTypes;
    }

    const legacyJobLocationTypes = job.locations
      .map((loc) => loc.locationType)
      .filter((locationType) => !(currentJobLocationTypes.includes(locationType)))
      .sort((a, b) => a.localeCompare(b));

    const orderedLocations = [
      ...currentJobLocationTypes,
      ...legacyJobLocationTypes,
    ];

    if (job?.locations?.length > 1) {
      orderedLocations.push('dock');
    }

    return orderedLocations;
  }
);

export const selectOrderedJobLocations = createSelector(
  selectJob,
  selectOrderedJobLocationTypes,
  (job, orderedLocationTypes) => {

    const jobLocations = job?.locations || [];

    if (jobLocations.length < 2) return jobLocations;

    const jobLocationsByType = new Map(jobLocations.map((loc) => [ loc.locationType, loc ]))

    return orderedLocationTypes
      .map((locationType) => jobLocationsByType.get(locationType))
      .filter(Boolean);
  }
);
